import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from "firebase/auth";
import { auth } from "./firebaseConfig";

// Self-contained, fully-localized handler for Firebase's email action links
// (verify e-mail / reset password / recover e-mail). Firebase's own hosted
// action page does NOT localize to Czech even with lang=cs, so we host our own
// and point the project's Action URL here. Styles are inline on purpose: this
// page must render correctly for non-technical / older users regardless of
// which CSS bundle happens to load.

const C = {
  bg: "#0f0f0f",
  card: "#1a1a1a",
  border: "rgba(255,255,255,0.10)",
  text: "#f2f2f2",
  sub: "#a8a8a8",
  accent: "#ff8c42",
  danger: "#e85c4a",
};

const page = {
  minHeight: "100vh",
  margin: 0,
  background: C.bg,
  color: C.text,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  fontFamily: "inherit",
  boxSizing: "border-box",
};
const card = {
  width: "100%",
  maxWidth: "420px",
  background: C.card,
  border: `1px solid ${C.border}`,
  borderRadius: "16px",
  padding: "32px 26px",
  textAlign: "center",
  boxSizing: "border-box",
};
const h1 = { fontSize: "1.5rem", margin: "0 0 12px", lineHeight: 1.25 };
const p = { fontSize: "1.05rem", color: C.sub, margin: "0 0 24px", lineHeight: 1.5 };
const btn = {
  display: "inline-block",
  width: "100%",
  boxSizing: "border-box",
  background: C.accent,
  color: "#1a1a1a",
  border: "none",
  borderRadius: "999px",
  padding: "15px 20px",
  fontSize: "1.1rem",
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "inherit",
};
const input = {
  width: "100%",
  boxSizing: "border-box",
  background: "#111",
  color: C.text,
  border: `1px solid ${C.border}`,
  borderRadius: "10px",
  padding: "14px",
  fontSize: "1.05rem",
  marginBottom: "14px",
  fontFamily: "inherit",
};


/** Který text ukázat podle toho, co Firebase vrátilo.
 *
 * Do 14. 9. 2026 tu byla jedna hláška na všechno: „Tento odkaz je neplatný nebo
 * už vypršel. Zkuste to prosím znovu." U vypršelého odkazu chyběla ta podstatná
 * půlka — že si má člověk nechat poslat NOVÝ. „Zkuste to znovu" se čte jako
 * „klikni na ten odkaz ještě jednou", což je přesně to, co nefunguje.
 *
 * A hlavně přibyla hláška o dočasném zámku. Firebase po několika neúspěšných
 * pokusech odmítá i SPRÁVNÉ heslo, a to je z pohledu člověka nepochopitelné:
 * ví, že heslo má dobře, a hra tvrdí, že ne. Míša (mtoni@seznam.cz) kvůli tomu
 * 14. 9. napsala, že se nemůže přihlásit „už poněkolikáté".
 */
export function chybovyText(code) {
  if (code === "auth/expired-action-code") return "authAction.errorExpired";
  if (code === "auth/invalid-action-code") return "authAction.errorUsed";
  if (code === "auth/too-many-requests") return "authAction.errorTooMany";
  if (code === "auth/weak-password") return "authAction.errorWeakPassword";
  return "authAction.errorBody";
}

export default function AuthActionHandler({ mode, oobCode, continueUrl }) {
  const { t } = useTranslation();
  // phase: working | success | error | resetForm
  const [phase, setPhase] = useState("working");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const goToApp = () => {
    window.location.href =
      continueUrl || window.location.origin + "/index.html";
  };

  useEffect(() => {
    if (!oobCode) {
      setPhase("error");
      setTitle(t("authAction.errorTitle"));
      setBody(t("authAction.errorBody"));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        if (mode === "resetPassword") {
          await verifyPasswordResetCode(auth, oobCode); // throws if invalid/expired
          if (cancelled) return;
          setPhase("resetForm");
          return;
        }
        // verifyEmail + recoverEmail both just apply the code
        await applyActionCode(auth, oobCode);
        if (cancelled) return;
        setPhase("success");
        if (mode === "recoverEmail") {
          setTitle(t("authAction.recoverDoneTitle"));
          setBody(t("authAction.recoverDoneBody"));
        } else {
          setTitle(t("authAction.verifiedTitle"));
          setBody(t("authAction.verifiedBody"));
        }
      } catch (e) {
        if (cancelled) return;
        setPhase("error");
        setTitle(t("authAction.errorTitle"));
        setBody(t(chybovyText(e && e.code)));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, oobCode]);

  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (password.length < 6) return;
    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setPhase("success");
      setTitle(t("authAction.resetDoneTitle"));
      setBody(t("authAction.resetDoneBody"));
    } catch (err) {
      setPhase("error");
      setTitle(t("authAction.errorTitle"));
      setBody(t(chybovyText(err && err.code)));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={page}>
      <div style={card}>
        <div style={{ fontSize: "2.4rem", marginBottom: "10px" }} aria-hidden>
          🔥
        </div>

        {phase === "working" && (
          <>
            <h1 style={h1}>{t("authAction.working")}</h1>
            <p style={p}>{t("authAction.workingBody")}</p>
          </>
        )}

        {phase === "resetForm" && (
          <form onSubmit={submitNewPassword}>
            <h1 style={h1}>{t("authAction.resetTitle")}</h1>
            <p style={p}>{t("authAction.resetBody")}</p>
            <input
              style={input}
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("authAction.newPasswordPlaceholder")}
              autoComplete="new-password"
            />
            <button
              style={{ ...btn, opacity: submitting || password.length < 6 ? 0.6 : 1 }}
              type="submit"
              disabled={submitting || password.length < 6}
            >
              {submitting ? t("authAction.working") : t("authAction.resetSubmit")}
            </button>
          </form>
        )}

        {(phase === "success" || phase === "error") && (
          <>
            <h1 style={{ ...h1, color: phase === "error" ? C.danger : C.text }}>
              {title}
            </h1>
            <p style={p}>{body}</p>
            <button style={btn} type="button" onClick={goToApp}>
              {t("authAction.continue")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
